import { WrapperWidget } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { T } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { Bath } from '~/generated/svg/Bath';
import { Floor } from '~/generated/svg/Floor';
import { Kitchen } from '~/generated/svg/Kitchen';
import { Masonry } from '~/generated/svg/Masonry';
import { Window } from '~/generated/svg/Window';

interface Props {
  readonly propertyQuality: Record<string, string>;
  readonly link: string;
}

const partComponents = {
  kitchen: Kitchen,
  bathrooms: Bath,
  windows: Window,
  flooring: Floor,
  masonry: Masonry,
};

const partOrder = [
  'bathrooms',
  'flooring',
  'kitchen',
  'masonry',
  'windows',
] as const;

const ratingScale = {
  simple: 0,
  normal: 1,
  high_quality: 2,
  luxury: 3,
};

export function PropertyQualityWidget(props: Props) {
  return (
    <WrapperWidget>
      <div className="flex flex-col h-full gap-y-4">
        <h2 className="text-lg font-bold">
          <T>evaluation_property_finishing_standard_widget_title</T>
        </h2>

        <p>
          <T>evaluation_property_finishing_standard_widget_text</T>
        </p>

        <div className="flex flex-col gap-2">
          {partOrder
            .filter(
              (part) =>
                props.propertyQuality[`quality_${part}`] &&
                props.propertyQuality[`quality_${part}`] !== 'not_defined',
            )
            .map((part) => {
              const rating =
                props.propertyQuality[`quality_${part}`] || 'not_defined';
              const PartComponent = partComponents[part];
              const ratingValue =
                ratingScale[rating as keyof typeof ratingScale] ?? -1;

              return (
                <div
                  key={part}
                  className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-3 rounded-lg border border-base-300 dark:border-transparent bg-base-100 dark:bg-base-800"
                >
                  <div className="flex items-center justify-center w-8 h-8 shrink-0 rounded-md bg-base-200 dark:bg-base-700 border border-base-300 dark:border-transparent">
                    {PartComponent && (
                      <PartComponent className="w-4 h-4 text-base-600 dark:text-base-200 [&_[fill='#333']]:fill-current [&_[stroke='#333']]:stroke-current" />
                    )}
                  </div>
                  <span className="text-sm font-medium min-w-0 truncate">
                    <T
                      keyName={`model_property_priceHubbleItem_${part}_name`}
                    />
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex gap-1">
                      {[
                        0,
                        1,
                        2,
                        3,
                      ].map((segment) => (
                        <div
                          key={segment}
                          className={`w-5 h-1.5 rounded-full ${
                            ratingValue === -1
                              ? 'bg-base-200 dark:bg-base-700'
                              : segment <= ratingValue
                              ? 'bg-primary-500 dark:bg-primary-400'
                              : 'bg-base-200 dark:bg-base-700'
                          }`}
                        />
                      ))}
                    </div>
                    <span
                      className={`text-xs font-semibold min-w-[72px] text-right whitespace-nowrap ${
                        ratingValue === -1
                          ? 'text-base-500 dark:text-base-400'
                          : 'text-primary-500 dark:text-primary-400'
                      }`}
                    >
                      <T
                        keyName={`model_property_priceHubbleQuality_value_${rating}`}
                      />
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        <AppLink
          to={props.link}
          className="self-start"
        >
          <Button
            primary
            showArrow
          >
            <T>ui_button_edit</T>
          </Button>
        </AppLink>
      </div>
    </WrapperWidget>
  );
}
